/**
 * Sell cart (DNKADM-0241) — the shared package + bucket-credit cart and checkout.
 *
 * ONE cart, ONE server quote, ONE charge. `package` and `credit` lines ride the same
 * three endpoints as a membership order (`/pricing/quote`, `/orders`,
 * `/orders/{orderId}`), so this dialog mirrors {@link BuyMembershipOrderDialog}'s
 * checkout mechanics throughout (location/tenant resolution, `CardPaymentField` +
 * `TokenizeFn`, the comp-PIN gate, the `attemptKeyRef` idempotency, the receipt).
 * `membership_plan` is NOT sellable here — mixing it is a `400 UNSUPPORTED_IN_SLICE`
 * and plans keep their own dialog.
 *
 * **The money rule this spec exists to enforce.** Every figure on screen is the
 * SERVER's: `subtotal`/`tax`/`total` come from the quote, per-row amounts come from
 * the quote's `lines[]` keyed on `(type, refId)`, and the receipt's `grandTotal` wins
 * over the quote if they differ. Nothing is recomputed client-side; an edited package
 * sends only its pre-tax `overrideBase` in minor units and the server recomputes every
 * displayed figure. When the quote reports `taxDeferred` (⇒ `tax`/`total` are `null`) the
 * footer shows the subtotal plus "Tax calculated at checkout" and NO tax/total figure,
 * and the pay button carries no figure at all — a `null` is never rendered as `$0.00`
 * and a pre-tax amount is never labelled as the charge.
 *
 * **What is sellable here (DNKADM-0245).** A package needs a POSITIVE price. `null` is
 * unpriced and `0` is a genuinely FREE package — free is given away with a COMP order,
 * never sold on a priced cart (Operator decision, 2026-08-04). That is a product rule,
 * not a parsing artifact: `Offering.price` now carries all three states distinctly and
 * the rule is applied here, at the point of sale (see {@link toPackageRow}).
 *
 * **Payment actions (DNKADM-0245).** A `PENDING` order's `paymentAction` is resolved
 * through the SHARED `./paymentActionDriver` seam in the same order the Buy dialog
 * uses; when nothing can drive it, the authoritative `getMemberOrder` re-read settles
 * the order and nothing claims success.
 *
 * **Selling to a card on file (DNKADM-0253).** In card mode the dialog offers the
 * member's saved cards through the SHARED {@link CardOnFileChooser} (the same component
 * the Buy dialog uses). Picking one hides the `CardPaymentField` — there is nothing to
 * tokenize — and the order carries `payment.cardId` instead of `payment.cardToken`;
 * exactly one credential is possible, because the two are mutually exclusive in the
 * type. Choosing "Use a new card" tokenizes as before and then **saves the card first**
 * (`saveMemberCard`, bound to the working `locationId`), ordering with the id that comes
 * back — so a card taken at the counter is on file for next time. A failed save issues
 * NO order and never falls back to a token-only charge: the admin asked to keep the
 * card, and a half-done outcome would be worse than a clear failure. Changing the
 * selected card is a cart edit like any other, so it starts a new attempt key. The
 * two `422 SAVED_CARD_CONNECTION_*` errors are membership-only by contract and are
 * deliberately NOT handled here — a one-time sale runs on the card's own connection.
 *
 * **A settled sale REPLACES the cart (DNKADM-0250).** Once the order is a receipt the
 * cart is gone — not merely frozen: the catalog rows, steppers, money footer, mode
 * selector and pay button are unrendered and a purchase confirmation takes their place,
 * headlined by the receipt's own `grandTotal`. The dialog's only remaining action is to
 * close it: this is a point-of-sale screen and it offers NO refund/void (Operator
 * decision, 2026-08-04 — reversal belongs to the transaction log, not the sell screen).
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { useManagerPinInputProps } from '@/components/useManagerPinInputProps';
import { ApiError } from '@/api/errors';
import { listCreditBuckets, type CreditBucket } from '@/api/creditBuckets';
import { saveMemberCard } from '@/api/memberCards';
import { listPackages, type Offering } from '@/api/offerings';
import { listPaymentMethods, type PaymentMethod } from '@/api/paymentMethods';
import {
  createMemberOrder,
  getMemberOrder,
  getMembershipQuote,
  type CreateMemberOrderInput,
  type MembershipOrder,
  type MembershipQuote,
  type MembershipQuoteLine,
  type OpenOrderNeedsAttention,
} from '@/api/membershipOrders';
import { CardTokenizationError, newIdempotencyKey } from '@/api/payments';
import { serverMessage } from '@/api/serverMessage';
import { declineReasonLineFromError } from '@/utils/declineReason';
import CardPaymentField, { type TokenizeFn } from '@/components/payments/CardPaymentField';
import { useCurrentOperator, useStaffTier } from '@/auth/authHooks';
import { useServiceLocation } from '@/location/useServiceLocation';
import { formatCreditAmount, formatMoney } from '@/utils/v2Format';
import { isNeedsReview, unsettledOrderMessage } from '@/domain/orderStatus';
import { OrderStatusRow } from '@/components/OrderStatusBadge';
import { scrubBrand } from '@/utils/whitelabel';
import { Icon, inkAlpha, MONO, resolveTokens } from '@/theme';
import { getPaymentActionDriver, UnsupportedPaymentActionError } from './paymentActionDriver';
import { CardOnFileChooser } from './CardOnFileChooser';
import {
  compOverrideAuth,
  compPinAllowsSubmit,
  compPinErrorView,
  resolveCompPinGate,
  compPinRequired,
  compPinShown,
  compChoiceShown,
  type CompPinGate,
} from './compPin';
import { NegotiatedPricePinPrompt } from './NegotiatedPricePinPrompt';
import {
  majorPriceInput,
  negotiatedPriceErrorView,
  negotiatedPriceToMinor,
  useNegotiatedPriceOverride,
} from './negotiatedPriceOverride';
import {
  CHECK_AGAIN_FAILED,
  outcomeIsUnknown,
  unconfirmedClosePrompt,
  unconfirmedMessage,
} from './unconfirmedOutcome';
import { OrderNeedsAttentionAlert } from './OrderNeedsAttentionAlert';
import { withOpenOrder } from './orderNeedsAttention';

/** The tenant currency is carried on the quote/receipt; this is the last-resort label. */
const FALLBACK_CURRENCY = 'USD';
const PLACEHOLDER = '-';

/**
 * The cart's line array and the three payment bodies, typed from the exported
 * create-order input — the line/payment input types are intentionally unexported by
 * `membershipOrders.ts`, so they are named here by projection rather than re-declared.
 */
type CartLines = CreateMemberOrderInput['lines'];
type CompPaymentInput = Extract<CreateMemberOrderInput['payment'], { mode: 'comp' }>;
type ExternalPaymentInput = Extract<CreateMemberOrderInput['payment'], { mode: 'external' }>;

type PaymentMode = 'card' | 'comp' | 'external';
type ExternalStatus = ExternalPaymentInput['status'];

export interface SellCartDialogProps {
  contactId: string;
  open: boolean;
  /** Which catalog to open on; BOTH sections are available once open. */
  initialFocus?: 'packages' | 'credits';
  /** Seed a credit bucket at qty 1 (the per-bucket "Sell" button passes this). */
  seedCreditBucketId?: string | null;
  onClose: () => void;
  onPurchased: () => void;
}

type CatalogState =
  | { status: 'idle' | 'loading' }
  | { status: 'ready'; buckets: CreditBucket[]; packages: Offering[] }
  | { status: 'error'; message: string };

type QuoteState =
  | { status: 'idle' | 'loading' }
  | { status: 'blocked' | 'awaiting-override-pin' }
  | { status: 'ready'; quote: MembershipQuote }
  | { status: 'error'; message: string; retryable: boolean };

type MethodsState =
  | { status: 'idle' | 'loading' }
  | { status: 'ready'; methods: PaymentMethod[] }
  | { status: 'error'; message: string };

interface SubmitError {
  message: string;
  retryable: boolean;
  /**
   * DNKADM-0475 (F-99): set on a `409 ORDER_NEEDS_ATTENTION` — an earlier card order of this
   * member is still open, so the alert names it and links to Payments instead of a charge.
   */
  openOrder?: OpenOrderNeedsAttention;
}

/**
 * DNKADM-0467: an attempt the server never confirmed either way. Its idempotency key is
 * kept and the cart and mode stay locked until "Check again" settles it under that key.
 */
interface Unconfirmed {
  message: string;
  /** The order the attempt produced, once known: "Check again" then reads it directly. */
  orderId: number | null;
  /** The saved card the attempt charged, so "Check again" re-sends the same credential. */
  cardId: string | null;
  /** Set when "Check again" could not read the order; the attempt stays locked. */
  readFailed?: boolean;
  /**
   * DNKADM-0475 (F-100): locked on a `NEEDS_REVIEW` order — the card was charged and nothing
   * applied — so the close prompt says so instead of "couldn't confirm".
   */
  needsReview?: boolean;
}

/** A sellable credit bucket, normalized for render + ordering. */
interface BucketRow {
  /** The bucket's UI id (`CreditBucket.id`) — the cart's key and the seed target. */
  key: string;
  /** The numeric `refId` a `credit` line carries. */
  refId: number;
  name: string;
  pricePerCredit: number;
  /**
   * The sanitized purchase cap, or `null` when uncapped. A server cap below 1 is
   * carried through as `0` — the row then cannot enter the cart at all (see
   * {@link sanitizeCap}).
   */
  cap: number | null;
  /** `soldCreditExpiryDays` rendered as a hint, or null when the server said nothing. */
  expiryHint: string | null;
}

/** A sellable package, normalized for render + ordering. */
interface PackageRow {
  key: string;
  refId: number;
  name: string;
  price: number;
  /** The inline by-bucket grants ("what's in it"), already brand-scrubbed. */
  grants: string[];
}

export function SellCartDialog({
  contactId,
  open,
  initialFocus,
  seedCreditBucketId,
  onClose,
  onPurchased,
}: SellCartDialogProps) {
  if (!open) return null;
  return (
    <SellCartDialogBody
      key={contactId}
      contactId={contactId}
      initialFocus={initialFocus ?? 'packages'}
      seedCreditBucketId={seedCreditBucketId ?? null}
      onClose={onClose}
      onPurchased={onPurchased}
    />
  );
}

function SellCartDialogBody({
  contactId,
  initialFocus,
  seedCreditBucketId,
  onClose,
  onPurchased,
}: {
  contactId: string;
  initialFocus: 'packages' | 'credits';
  seedCreditBucketId: string | null;
  onClose: () => void;
  onPurchased: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const ids = useId();
  const titleId = `${ids}-title`;
  const reasonId = `${ids}-reason`;
  const externalStatusId = `${ids}-external-status`;
  const externalMethodId = `${ids}-external-method`;
  const location = useServiceLocation();
  const operator = useCurrentOperator();
  const tenantSlug = operator?.tenantSlug?.trim() ?? '';

  const [catalog, setCatalog] = useState<CatalogState>({ status: 'idle' });
  const [quantities, setQuantities] = useState<Record<string, number>>(() =>
    seedCreditBucketId === null || seedCreditBucketId.length === 0
      ? {}
      : { [seedCreditBucketId]: 1 },
  );
  const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>([]);
  /** Presence of a key means the package price was edited; absence means list price. */
  const [packagePriceEdits, setPackagePriceEdits] = useState<Record<string, string>>({});
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('card');
  const [quoteState, setQuoteState] = useState<QuoteState>({ status: 'idle' });
  const [quoteCurrency, setQuoteCurrency] = useState(FALLBACK_CURRENCY);
  const [reason, setReason] = useState('');
  const isStaffTier = useStaffTier();
  const [compPin, setCompPin] = useState('');
  const [compPinGate, setCompPinGate] = useState<CompPinGate>({ status: 'idle' });
  /** The server's refusal of the last comp PIN, shown on the field. */
  const [compPinError, setCompPinError] = useState<string | null>(null);
  /** A PIN refusal keeps the field on screen even where the connection read said no PIN. */
  const [compPinAsked, setCompPinAsked] = useState(false);
  const [methodsState, setMethodsState] = useState<MethodsState>({ status: 'idle' });
  const [externalStatus, setExternalStatus] = useState<ExternalStatus>('paid');
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [cardReady, setCardReady] = useState(false);
  /** The chosen card on file, or `null` while the admin is entering a new one. */
  const [cardOnFileId, setCardOnFileId] = useState<string | null>(null);
  const [paymentNote, setPaymentNote] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<SubmitError | null>(null);
  const [receipt, setReceipt] = useState<MembershipOrder | null>(null);
  const [unconfirmed, setUnconfirmed] = useState<Unconfirmed | null>(null);
  const attemptKeyRef = useRef<string | null>(null);
  const tokenizeRef = useRef<TokenizeFn | null>(null);
  const quoteSeqRef = useRef(0);

  const locationId = location.selectedId !== null ? Number(location.selectedId) : Number.NaN;
  const locationReady = location.status === 'ready' && Number.isFinite(locationId);
  const tenantReady = tenantSlug.length > 0;

  const loadCatalog = useCallback(() => {
    setCatalog({ status: 'loading' });
    // Both catalogs in parallel; either failing is one error + Retry, never a
    // half-catalog that silently hides sellable rows.
    void Promise.all([listCreditBuckets({ status: 'active' }), listPackages('active')])
      .then(([buckets, packages]) => setCatalog({ status: 'ready', buckets, packages }))
      .catch((error: unknown) =>
        setCatalog({
          status: 'error',
          message: serverMessage(error) ?? 'The sellable catalog could not be loaded.',
        }),
      );
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const bucketRows = useMemo<BucketRow[]>(
    () => (catalog.status === 'ready' ? catalog.buckets.flatMap(toBucketRow) : []),
    [catalog],
  );
  const packageRows = useMemo<PackageRow[]>(
    () =>
      catalog.status === 'ready'
        ? catalog.packages.flatMap((offering) => toPackageRow(offering, location.selectedId))
        : [],
    [catalog, location.selectedId],
  );
  const catalogEmpty =
    catalog.status === 'ready' && bucketRows.length === 0 && packageRows.length === 0;

  /**
   * The cart, built ONLY from rows that are actually sellable and visible: a hidden
   * (unpriced / off-location / uncapped-to-zero) row can never contribute a line, and
   * a bucket contributes at most ONE line — duplicate `refId`s are merged server-side
   * and the cap applies to the summed quantity.
   */
  const lines = useMemo<CartLines>(() => {
    const out: CartLines = [];
    for (const row of bucketRows) {
      const qty = quantityFor(quantities, row);
      if (qty >= 1) out.push({ type: 'credit', refId: row.refId, qty });
    }
    for (const row of packageRows) {
      if (!selectedPackageIds.includes(row.key)) continue;
      const edit = paymentMode === 'comp' ? undefined : packagePriceEdits[row.key];
      const overrideBase = edit === undefined ? null : negotiatedPriceToMinor(edit, quoteCurrency);
      out.push(
        overrideBase === null
          ? { type: 'package', refId: row.refId }
          : { type: 'package', refId: row.refId, overrideBase },
      );
    }
    return out;
  }, [
    bucketRows,
    packageRows,
    packagePriceEdits,
    paymentMode,
    quantities,
    quoteCurrency,
    selectedPackageIds,
  ]);

  const hasInvalidPackagePrice =
    paymentMode !== 'comp' &&
    packageRows.some((row) => {
      if (!selectedPackageIds.includes(row.key)) return false;
      const edit = packagePriceEdits[row.key];
      return edit !== undefined && negotiatedPriceToMinor(edit, quoteCurrency) === null;
    });
  const hasNegotiatedPrice = lines.some(
    (line) => line.type === 'package' && line.overrideBase !== undefined,
  );
  const negotiatedPrice = useNegotiatedPriceOverride(hasNegotiatedPrice);
  const overrideAuth = negotiatedPrice.overrideAuth;

  const quoteLocationId = locationReady ? locationId : undefined;

  const loadQuote = useCallback(() => {
    // An empty cart is not a quotable cart — issue no request at all.
    if (lines.length === 0) {
      quoteSeqRef.current += 1;
      setQuoteState({ status: 'idle' });
      return;
    }
    if (hasInvalidPackagePrice) {
      quoteSeqRef.current += 1;
      setQuoteState({ status: 'blocked' });
      return;
    }
    if (hasNegotiatedPrice && overrideAuth === undefined) {
      quoteSeqRef.current += 1;
      setQuoteState({ status: 'awaiting-override-pin' });
      return;
    }
    const seq = quoteSeqRef.current + 1;
    quoteSeqRef.current = seq;
    setQuoteState({ status: 'loading' });
    void getMembershipQuote(contactId, lines, quoteLocationId, overrideAuth)
      .then((quote) => {
        // A stale response can never overwrite a newer edit's quote.
        if (quoteSeqRef.current !== seq) return;
        setQuoteCurrency(quote.currency ?? FALLBACK_CURRENCY);
        setQuoteState({ status: 'ready', quote });
      })
      .catch((error: unknown) => {
        if (quoteSeqRef.current !== seq) return;
        const view = toSellErrorView(error, 'The cart could not be priced.');
        setQuoteState({ status: 'error', message: view.message, retryable: view.retryable });
      });
  }, [contactId, hasInvalidPackagePrice, hasNegotiatedPrice, lines, overrideAuth, quoteLocationId]);

  useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  // The comp-PIN gate, exactly as the Buy dialog resolves it.
  useEffect(() => {
    setCompPinError(null);
    setCompPinAsked(false);
    if (!locationReady) {
      setCompPinGate({ status: 'idle' });
      setCompPin('');
      return;
    }
    let cancelled = false;
    setCompPin('');
    setCompPinGate({ status: 'loading' });
    void resolveCompPinGate(isStaffTier, location.selectedId ?? undefined).then((gate) => {
      if (cancelled) return;
      setCompPinGate(gate);
      // DNKADM-0476 F-62: where the Comp choice is not offered, a comp in progress falls back to card.
      if (!compChoiceShown(gate, isStaffTier)) setPaymentMode((mode) => (mode === 'comp' ? 'card' : mode));
    });
    return () => {
      cancelled = true;
    };
  }, [isStaffTier, location.selectedId, locationReady]);

  // The external method picker is loaded only when external mode is chosen.
  useEffect(() => {
    if (paymentMode !== 'external') return;
    let cancelled = false;
    setMethodsState({ status: 'loading' });
    void listPaymentMethods('active')
      .then((methods) => {
        if (cancelled) return;
        setMethodsState({ status: 'ready', methods });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setMethodsState({
          status: 'error',
          message: serverMessage(error) ?? 'Payment methods could not be loaded.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [paymentMode]);

  const quote = quoteState.status === 'ready' ? quoteState.quote : null;
  const currency = quote?.currency ?? quoteCurrency;
  /** A settled sale: the cart is done and the confirmation replaces it. */
  const sold = receipt !== null;
  // The cart's own freeze: in flight, or an attempt whose outcome is unconfirmed
  // (DNKADM-0467). A settled sale no longer renders these controls at all (DNKADM-0250),
  // so there is nothing left to disable for it.
  const locked = submitting || unconfirmed !== null;

  // DNKADM-0400: the server checks the comp PIN; the console only decides whether to ask.
  const compPinFieldRequired = compPinRequired(compPinGate);
  const compPinFieldShown = compPinAsked || compPinShown(compPinGate);
  const compGateAllowsSubmit = compPinAllowsSubmit(compPinGate, compPin);

  // A card on file needs no tokenizer — there is nothing left to enter (DNKADM-0253).
  const cardReadyToSubmit =
    tenantReady && (cardOnFileId !== null || (cardReady && tokenizeRef.current !== null));
  // External records HOW it was paid, never how much: a `paid` record must name the
  // method it was paid with; `pending` names none.
  const externalReadyToSubmit =
    externalStatus === 'pending' || paymentMethodId.trim().length > 0;
  const modeReadyToSubmit =
    paymentMode === 'card'
      ? cardReadyToSubmit
      : paymentMode === 'comp'
        ? compGateAllowsSubmit
        : externalReadyToSubmit;
  const canSubmit =
    lines.length > 0 &&
    quoteState.status === 'ready' &&
    (!hasNegotiatedPrice || overrideAuth !== undefined) &&
    locationReady &&
    modeReadyToSubmit &&
    unconfirmed === null &&
    !submitting;

  /** Any cart/mode edit starts a NEW sale: a fresh idempotency key, no stale receipt. */
  function clearAttemptForEdit() {
    attemptKeyRef.current = null;
    setSubmitError(null);
    setReceipt(null);
    setPaymentNote(null);
  }

  function setQuantity(row: BucketRow, next: number) {
    clearAttemptForEdit();
    negotiatedPrice.invalidate();
    const bounded = boundQuantity(next, row.cap);
    setQuantities((prev) => ({ ...prev, [row.key]: bounded }));
  }

  function togglePackage(row: PackageRow, selected: boolean) {
    clearAttemptForEdit();
    negotiatedPrice.invalidate();
    if (!selected) {
      setPackagePriceEdits((prev) => {
        const next = { ...prev };
        delete next[row.key];
        return next;
      });
    }
    setSelectedPackageIds((prev) =>
      selected ? (prev.includes(row.key) ? prev : [...prev, row.key]) : prev.filter((id) => id !== row.key),
    );
  }

  function setPackagePrice(row: PackageRow, value: string) {
    clearAttemptForEdit();
    negotiatedPrice.invalidate();
    setPackagePriceEdits((prev) => ({ ...prev, [row.key]: value }));
  }

  function applyNegotiatedPrice() {
    clearAttemptForEdit();
    negotiatedPrice.apply();
  }

  function close() {
    // A charge in flight is un-dismissable: closing mid-submit would orphan the
    // outcome of a call that has already left the browser.
    if (submitting) return;
    // DNKADM-0467: an unconfirmed attempt may still land, and a sale started after closing
    // would carry a new key — so closing is confirmed, never silently allowed.
    if (unconfirmed !== null && !window.confirm(unconfirmedClosePrompt(unconfirmed.needsReview))) return;
    onClose();
  }

  async function submit() {
    setSubmitError(null);
    setPaymentNote(null);
    if (lines.length === 0) {
      setSubmitError({ retryable: false, message: 'Add at least one item to the cart.' });
      return;
    }
    if (!locationReady) {
      setSubmitError({ retryable: false, message: locationMessage(location.status) });
      return;
    }
    if (hasNegotiatedPrice && overrideAuth === undefined) {
      setSubmitError({
        retryable: false,
        message: 'Apply the negotiated price before placing this order.',
      });
      return;
    }
    if (paymentMode === 'comp' && !compGateAllowsSubmit) return;
    if (paymentMode === 'external' && !externalReadyToSubmit) {
      setSubmitError({
        retryable: false,
        message: 'Choose the payment method this sale was paid with.',
      });
      return;
    }
    if (paymentMode === 'card') {
      await submitCardOrder();
      return;
    }
    await submitNonCardOrder();
  }

  async function submitCardOrder() {
    if (!tenantReady) {
      setSubmitError({
        retryable: false,
        message: 'Tenant payment context is unavailable. Sign in again before charging a card.',
      });
      return;
    }
    if (quoteState.status !== 'ready') {
      setSubmitError({ retryable: true, message: 'Pricing quote must load before charging.' });
      return;
    }
    if (cardOnFileId === null && (!cardReady || tokenizeRef.current === null)) {
      setSubmitError({ retryable: false, message: 'Finish entering the card before charging.' });
      return;
    }

    setSubmitting(true);
    // Whichever way the card arrived, the order names a card ON FILE: a new card is
    // SAVED FIRST, so the counter card is there next time. A failed save ends the
    // attempt here — no order is issued and there is no token-only fallback.
    let cardId = cardOnFileId;
    if (cardId === null) {
      try {
        cardId = await saveEnteredCard();
      } catch (error) {
        setSubmitError(
          toSellErrorView(error, 'The card could not be saved, so nothing was charged.'),
        );
        setSubmitting(false);
        return;
      }
    }
    await placeOrder(cardId);
  }

  /**
   * Tokenize the entered card and SAVE it, returning the saved `cardId` the order is
   * then charged to. The nonce is single-use and local — never stored or logged.
   *
   * A failed save throws, so the caller issues **no order at all**: falling back to a
   * token-only charge would silently drop the card the admin chose to keep, and would
   * leave a charge whose card is nowhere on file. A save that answers without a usable
   * id is the same situation — the nonce is already spent, so there is nothing to
   * charge and nothing is attempted.
   */
  async function saveEnteredCard(): Promise<string> {
    if (tokenizeRef.current === null) throw new Error('Card entry is not ready.');
    const cardToken = await tokenizeRef.current();
    // `reused: true` is a SUCCESS — the member simply already had this card on file.
    const saved = await saveMemberCard(contactId, cardToken, locationId);
    if (saved.cardId.length === 0) {
      throw new Error('The card was saved but could not be identified, so nothing was charged.');
    }
    return saved.cardId;
  }

  async function submitNonCardOrder() {
    await placeOrder(null);
  }

  /**
   * Send the attempt's order — card (`cardId` set), comp or external (`cardId` null). One
   * key per ATTEMPT, reused on a retry and on "Check again": the server answers a key it
   * has seen with the order that key already produced, never a second charge or grant.
   */
  async function placeOrder(cardId: string | null) {
    const idempotencyKey = attemptKeyRef.current ?? newIdempotencyKey();
    attemptKeyRef.current = idempotencyKey;
    const comp = paymentMode === 'comp';
    // A comp carries no negotiated price (its lines drop `overrideBase`), so the one
    // request-level `overrideAuth` is the manager PIN the server checks (DNKADM-0400).
    const requestAuth = comp ? compOverrideAuth(compPin) : overrideAuth;
    setSubmitting(true);
    try {
      const order = await createMemberOrder(contactId, {
        lines,
        payment:
          cardId !== null
            ? { mode: 'card', cardId, locationId, idempotencyKey }
            : comp
              ? compPayment(locationId, idempotencyKey, reason)
              : externalPayment(locationId, idempotencyKey, externalStatus, paymentMethodId),
        ...(requestAuth === undefined ? {} : { overrideAuth: requestAuth }),
      });
      await settleOrder(order, cardId);
    } catch (error) {
      const pinView = comp ? compPinErrorView(error) : null;
      if (pinView !== null) {
        // The server refused the PIN before doing anything: a fresh attempt next time.
        // Not after an unconfirmed send of this key, though — that send may still land,
        // so the key and the lock stay, and a PIN can be entered for "Check again".
        if (unconfirmed === null) attemptKeyRef.current = null;
        setCompPinAsked(true);
        setCompPinError(pinView.message);
        return;
      }
      if (outcomeIsUnknown(error)) {
        // DNKADM-0467: the server may have committed this order. Keep the key and lock
        // the cart; only "Check again", under this same key, can settle it.
        setUnconfirmed({
          message: unconfirmedMessage(paymentMode),
          orderId: unconfirmed?.orderId ?? null,
          cardId,
        });
        return;
      }
      // A definitive refusal: nothing was written, so the next submit is a new attempt.
      // DNKADM-0458: the negotiated-price PIN ends with the attempt too.
      attemptKeyRef.current = null;
      setUnconfirmed(null);
      if (!comp && overrideAuth !== undefined) negotiatedPrice.reset();
      setSubmitError(withOpenOrder(error, toSellErrorView(error)));
    } finally {
      // The PIN is never kept after a submit, whatever the outcome.
      if (comp) setCompPin('');
      setSubmitting(false);
    }
  }

  /**
   * DNKADM-0467: settle an unconfirmed attempt under its OWN key before anything new is
   * allowed. Once the order is known it is read; until then the same request is re-sent.
   */
  async function checkAgain() {
    if (unconfirmed === null || submitting) return;
    setSubmitError(null);
    const { orderId, cardId } = unconfirmed;
    if (orderId === null) {
      await placeOrder(cardId);
      return;
    }
    setSubmitting(true);
    try {
      finishOrder(await getMemberOrder(contactId, orderId), cardId);
    } catch {
      // A failed read settles nothing: the attempt stays unconfirmed under its key.
      setUnconfirmed((current) => (current === null ? null : { ...current, readFailed: true }));
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * The three outcomes, identical for card / comp / external. A `PENDING` order that
   * carries a `paymentAction` is driven and then RE-READ — the read is authoritative,
   * and `paymentAction` being `null` on it is not a state change.
   */
  async function settleOrder(order: MembershipOrder, cardId: string | null) {
    const terminal =
      order.status === 'PENDING' && order.paymentAction !== null
        ? await settlePendingOrder(order)
        : order;
    finishOrder(terminal, cardId);
  }

  function finishOrder(terminal: MembershipOrder, cardId: string | null) {
    if (terminal.status === 'COMPLETED') {
      attemptKeyRef.current = null;
      setUnconfirmed(null);
      setPaymentNote(null);
      setReceipt(terminal);
      onPurchased();
      return;
    }
    // Not settled, so not terminal (DNKADM-0467): nothing was granted yet, and the
    // attempt keeps its key until "Check again" reads it settled. NEEDS_REVIEW is held
    // the same way (DNKADM-0472): the card was charged and nothing applied, so a fresh
    // key here would let the desk charge again.
    if (terminal.status === 'PENDING' || isNeedsReview(terminal.status)) {
      setUnconfirmed({
        message: terminalOrderMessage(terminal),
        orderId: terminal.orderId,
        cardId,
        needsReview: isNeedsReview(terminal.status),
      });
      return;
    }
    // No receipt on a decline/failure order — nothing was granted. A terminal read ends
    // the attempt.
    attemptKeyRef.current = null;
    setUnconfirmed(null);
    setSubmitError({ retryable: false, message: terminalOrderMessage(terminal) });
  }

  async function settlePendingOrder(order: MembershipOrder): Promise<MembershipOrder> {
    setPaymentNote(paymentActionNote(order.paymentAction));
    try {
      await drivePaymentAction(order.paymentAction);
    } catch (error) {
      // No driver registered for this action — NOT a failure here. The sell cart's
      // authority is the re-read below, so an unsupported (or unrecognized) action
      // simply falls through to it; nothing is ever claimed as driven.
      if (!(error instanceof UnsupportedPaymentActionError)) throw error;
    }
    if (order.orderId === null) return order;
    return getMemberOrder(contactId, order.orderId);
  }

  const creditsSection = (
    <CartSection
      heading="Credits"
      empty="No sellable credit buckets."
      show={bucketRows.length > 0 || catalog.status === 'ready'}
    >
      {bucketRows.map((row) => (
        <BucketCartRow
          key={row.key}
          row={row}
          qty={quantityFor(quantities, row)}
          currency={currency}
          amount={rowAmountLabel(quote, 'credit', row.refId, currency)}
          disabled={locked}
          onChange={(next) => setQuantity(row, next)}
        />
      ))}
    </CartSection>
  );

  const packagesSection = (
    <CartSection
      heading="Packages"
      empty="No sellable packages at this location."
      show={packageRows.length > 0 || catalog.status === 'ready'}
    >
      {packageRows.map((row) => (
        <PackageCartRow
          key={row.key}
          row={row}
          selected={selectedPackageIds.includes(row.key)}
          currency={currency}
          quoteLine={quote === null ? null : findQuoteLine(quote, 'package', row.refId)}
          priceEdit={paymentMode === 'comp' ? undefined : packagePriceEdits[row.key]}
          priceInvalid={
            paymentMode !== 'comp' &&
            packagePriceEdits[row.key] !== undefined &&
            negotiatedPriceToMinor(packagePriceEdits[row.key] ?? '', currency) === null
          }
          allowPriceOverride={paymentMode !== 'comp'}
          disabled={locked}
          onToggle={(selected) => togglePackage(row, selected)}
          onPriceChange={(value) => setPackagePrice(row, value)}
        />
      ))}
    </CartSection>
  );

  const compPinInputProps = useManagerPinInputProps(compPin, (pin) => {
    // A PIN typed for "Check again" belongs to the unconfirmed attempt
    // and must not start a new one (DNKADM-0467).
    if (unconfirmed === null) clearAttemptForEdit();
    setCompPinError(null);
    setCompPin(pin);
  });

  return (
    <Dialog open onClose={close} maxWidth="sm" fullWidth aria-labelledby={titleId}>
      {/* The header carries its OWN id: MUI would otherwise stamp the dialog's
          `aria-labelledby` id onto the <h2> too, and the name would then include the
          icon ligature, the subtitle and the close button instead of the title. */}
      <DialogTitle
        id={`${ids}-header`}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}
      >
        <Avatar
          variant="rounded"
          sx={{ bgcolor: t.brandSoft, color: t.brand, width: 38, height: 38 }}
        >
          <Icon name="shopping_cart" sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id={titleId} sx={{ fontSize: 18, fontWeight: 500 }}>
            Sell packages and credits
          </Typography>
          <Typography variant="body2">Card, comp, or external payment</Typography>
        </Box>
        <IconAction size="small" onClick={close} label="Close">
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>

      <DialogContent>
        {/* A settled sale REPLACES the cart: the rows, steppers, money footer, mode
            selector and pay button are not rendered at all (DNKADM-0250). */}
        {sold ? (
          <SellConfirmation order={receipt} />
        ) : (
          <Stack spacing={2}>
            {!locationReady && (
              <Alert
                severity="warning"
                action={
                  location.status === 'error' ? (
                    <Button color="inherit" size="small" onClick={location.retry}>
                      Retry
                    </Button>
                  ) : undefined
                }
              >
                {locationMessage(location.status)}
              </Alert>
            )}

            {catalog.status === 'loading' && (
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Loading what can be sold...</Typography>
              </Stack>
            )}

            {catalog.status === 'error' && (
              <Alert
                severity="error"
                role="alert"
                action={
                  <Button color="inherit" size="small" onClick={loadCatalog}>
                    Retry
                  </Button>
                }
              >
                {catalog.message}
              </Alert>
            )}

            {catalogEmpty ? (
              <Alert severity="info">
                Nothing can be sold to this member yet. Price a credit bucket or publish a package
                for this location first.
              </Alert>
            ) : initialFocus === 'credits' ? (
              <>
                {creditsSection}
                {packagesSection}
              </>
            ) : (
              <>
                {packagesSection}
                {creditsSection}
              </>
            )}

            {!catalogEmpty && (
              <MoneyFooter quoteState={quoteState} quoteRetry={loadQuote} empty={lines.length === 0} />
            )}

            {hasNegotiatedPrice && paymentMode !== 'comp' && (
              <NegotiatedPricePinPrompt
                draftPin={negotiatedPrice.draftPin}
                disabled={locked}
                onDraftChange={(value) => {
                  clearAttemptForEdit();
                  negotiatedPrice.setDraftPin(value);
                }}
                onApply={applyNegotiatedPrice}
              />
            )}

            <ToggleButtonGroup
              exclusive
              size="small"
              value={paymentMode}
              onChange={(_event, value: PaymentMode | null) => {
                if (value === null || value === paymentMode || locked) return;
                clearAttemptForEdit();
                setCompPin('');
                setCardReady(false);
                setCardOnFileId(null);
                tokenizeRef.current = null;
                if (value === 'comp') {
                  setPackagePriceEdits({});
                  negotiatedPrice.reset();
                }
                setPaymentMode(value);
              }}
              aria-label="Payment mode"
              sx={{ alignSelf: 'flex-start' }}
            >
              <ToggleButton value="card" aria-label="Pay card">
                Pay card
              </ToggleButton>
              {compChoiceShown(compPinGate, isStaffTier) && (
                <ToggleButton value="comp" aria-label="Comp">
                  Comp
                </ToggleButton>
              )}
              <ToggleButton value="external" aria-label="External">
                External
              </ToggleButton>
            </ToggleButtonGroup>

            {paymentMode === 'card' &&
              (locationReady && tenantReady ? (
                <>
                  {/* The shared chooser (DNKADM-0253) — mounted only while this dialog
                      is open in card mode, so its list is never cached beyond a sale. */}
                  <CardOnFileChooser
                    contactId={contactId}
                    value={cardOnFileId}
                    onChange={(next) => {
                      // Changing the credential is a cart edit: a NEW attempt key, and
                      // any card entry in progress is dropped with it.
                      clearAttemptForEdit();
                      setCardOnFileId(next);
                      if (next !== null) {
                        tokenizeRef.current = null;
                        setCardReady(false);
                      }
                    }}
                    disabled={locked}
                  />
                  {/* Nothing to tokenize once a card on file is chosen. */}
                  {cardOnFileId === null && (
                    <CardPaymentField
                      tenantSlug={tenantSlug}
                      locationRef={location.selectedId ?? ''}
                      disabled={locked}
                      onReady={(tokenize) => {
                        tokenizeRef.current = tokenize;
                        setCardReady(true);
                      }}
                      onError={() => {
                        tokenizeRef.current = null;
                        setCardReady(false);
                      }}
                    />
                  )}
                </>
              ) : (
                <Alert severity="warning">
                  {tenantReady
                    ? 'Select a location before charging the card.'
                    : 'Tenant payment context is unavailable. Sign in again before charging a card.'}
                </Alert>
              ))}

            {paymentMode === 'comp' && (
              <>
                <TextField
                  id={reasonId}
                  label="Reason (optional)"
                  fullWidth
                  size="small"
                  value={reason}
                  onChange={(event) => {
                    clearAttemptForEdit();
                    setReason(event.target.value);
                  }}
                  disabled={locked}
                />

                {compPinFieldShown && (
                  <TextField
                    label="Manager PIN to comp"
                    required={compPinFieldRequired}
                    fullWidth
                    size="small"
                    {...compPinInputProps}
                    disabled={submitting}
                    error={compPinError !== null}
                    helperText={
                      compPinError ??
                      (compPinFieldRequired
                        ? isStaffTier
                          ? 'Staff need a manager to enter the PIN for this.'
                          : ' '
                        : 'Leave blank if this location has no manager PIN.')
                    }
                  />
                )}

                {compPinGate.status === 'loading' && (
                  <Typography variant="caption" sx={{ color: inkAlpha(theme, 0.62) }}>
                    Checking manager PIN requirement...
                  </Typography>
                )}
              </>
            )}

            {paymentMode === 'external' && (
              <>
                <TextField
                  id={externalStatusId}
                  label="Payment status"
                  select
                  required
                  fullWidth
                  size="small"
                  value={externalStatus}
                  onChange={(event) => {
                    clearAttemptForEdit();
                    setExternalStatus(event.target.value === 'pending' ? 'pending' : 'paid');
                  }}
                  disabled={locked}
                  helperText="External records how the sale was paid, never how much."
                >
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </TextField>

                {externalStatus === 'paid' && (
                  <TextField
                    id={externalMethodId}
                    label="Payment method"
                    select
                    required
                    fullWidth
                    size="small"
                    value={
                      methodsState.status === 'ready' &&
                      methodsState.methods.some((method) => method.id === paymentMethodId)
                        ? paymentMethodId
                        : ''
                    }
                    onChange={(event) => {
                      clearAttemptForEdit();
                      setPaymentMethodId(event.target.value);
                    }}
                    disabled={locked || methodsState.status !== 'ready'}
                    helperText={methodsState.status === 'error' ? methodsState.message : ' '}
                    error={methodsState.status === 'error'}
                  >
                    {methodsState.status === 'loading' && (
                      <MenuItem value="" disabled>
                        Loading payment methods...
                      </MenuItem>
                    )}
                    {methodsState.status === 'ready' && methodsState.methods.length === 0 && (
                      <MenuItem value="" disabled>
                        No payment methods available
                      </MenuItem>
                    )}
                    {methodsState.status === 'ready' &&
                      methodsState.methods.map((method) => (
                        <MenuItem key={method.id} value={method.id}>
                          {scrubBrand(method.label) ?? PLACEHOLDER}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
              </>
            )}

            {paymentNote && <Alert severity="info">{paymentNote}</Alert>}

            {unconfirmed !== null && (
              <Alert
                severity="warning"
                role="alert"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => void checkAgain()}
                    disabled={submitting}
                  >
                    Check again
                  </Button>
                }
              >
                {unconfirmed.message}
                {unconfirmed.readFailed === true && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {CHECK_AGAIN_FAILED}
                  </Typography>
                )}
              </Alert>
            )}

            {submitError?.openOrder !== undefined && (
              <OrderNeedsAttentionAlert
                openOrder={submitError.openOrder}
                contactId={contactId}
                onLeave={close}
              />
            )}
            {submitError && submitError.openOrder === undefined && (
              <Alert
                severity="error"
                role="alert"
                action={
                  submitError.retryable ? (
                    <Button color="inherit" size="small" onClick={() => void submit()}>
                      Retry
                    </Button>
                  ) : undefined
                }
              >
                {submitError.message}
              </Alert>
            )}

          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        {/* A settled sale leaves exactly ONE action: dismiss the confirmation. This
            screen offers no refund/void at all (DNKADM-0250). */}
        <Button
          color="inherit"
          onClick={close}
          disabled={submitting}
          sx={{ color: t.text2 }}
        >
          {sold ? 'Done' : 'Cancel'}
        </Button>
        {!sold && (
          <Button
            variant="contained"
            startIcon={<Icon name="check" />}
            onClick={() => void submit()}
            disabled={!canSubmit}
          >
            {submitButtonLabel(paymentMode, submitting, quote)}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// --- Rows ----------------------------------------------------------------------

function CartSection({
  heading,
  empty,
  show,
  children,
}: {
  heading: string;
  empty: string;
  show: boolean;
  children: ReactNode[];
}) {
  const theme = useTheme();
  if (!show) return null;
  return (
    <Stack spacing={1}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: inkAlpha(theme, 0.62) }}>
        {heading}
      </Typography>
      {children.length === 0 ? (
        <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.62) }}>
          {empty}
        </Typography>
      ) : (
        children
      )}
    </Stack>
  );
}

function BucketCartRow({
  row,
  qty,
  currency,
  amount,
  disabled,
  onChange,
}: {
  row: BucketRow;
  qty: number;
  currency: string;
  amount: string | null;
  disabled: boolean;
  onChange: (next: number) => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const atCap = row.cap !== null && qty >= row.cap;
  return (
    <Box sx={{ border: `1px solid ${t.line}`, borderRadius: 1, p: 1.25, bgcolor: t.surface }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{row.name}</Typography>
          <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.62) }}>
            {`${formatMoney(row.pricePerCredit, currency)} per credit`}
            {row.expiryHint === null ? '' : ` · ${row.expiryHint}`}
            {row.cap === null ? '' : ` · limit ${row.cap} per sale`}
          </Typography>
        </Box>
        <Stack
          direction="row"
          role="group"
          aria-label={`${row.name} quantity`}
          sx={{ alignItems: 'center', gap: 0.5 }}
        >
          <IconAction
            size="small"
            label={`Remove ${formatCreditAmount(1, row.name)}`}
            onClick={() => onChange(qty - 1)}
            disabled={disabled || qty <= 0}
          >
            <Icon name="remove" sx={{ fontSize: 18 }} />
          </IconAction>
          <Typography sx={{ minWidth: 24, textAlign: 'center', fontFamily: MONO, fontSize: 14 }}>
            {qty}
          </Typography>
          <IconAction
            size="small"
            label={`Add ${formatCreditAmount(1, row.name)}`}
            onClick={() => onChange(qty + 1)}
            disabled={disabled || atCap}
          >
            <Icon name="add" sx={{ fontSize: 18 }} />
          </IconAction>
        </Stack>
        <Typography sx={{ minWidth: 76, textAlign: 'right', fontSize: 13 }}>
          {amount ?? ''}
        </Typography>
      </Stack>
    </Box>
  );
}

function PackageCartRow({
  row,
  selected,
  currency,
  quoteLine,
  priceEdit,
  priceInvalid,
  allowPriceOverride,
  disabled,
  onToggle,
  onPriceChange,
}: {
  row: PackageRow;
  selected: boolean;
  currency: string;
  quoteLine: MembershipQuoteLine | null;
  priceEdit: string | undefined;
  priceInvalid: boolean;
  allowPriceOverride: boolean;
  disabled: boolean;
  onToggle: (selected: boolean) => void;
  onPriceChange: (value: string) => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  return (
    <Box sx={{ border: `1px solid ${t.line}`, borderRadius: 1, p: 1.25, bgcolor: t.surface }}>
      <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={selected}
                disabled={disabled}
                onChange={(event) => onToggle(event.target.checked)}
              />
            }
            label={row.name}
            slotProps={{ typography: { sx: { fontSize: 14, fontWeight: 500 } } }}
          />
          <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.62), pl: 3.75 }}>
            {formatMoney(row.price, currency)}
            {row.grants.length === 0 ? '' : ` · ${row.grants.join(', ')}`}
          </Typography>
          {quoteLine !== null && (
            <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.62), pl: 3.75 }}>
              {`Quoted price ${formatMoney(quoteLine.base, quoteLine.currency ?? currency)}`}
              {quoteLine.tax === null || quoteLine.total === null
                ? ' · Tax calculated at checkout'
                : ` · Tax ${formatMoney(quoteLine.tax, quoteLine.currency ?? currency)} · Total ${formatMoney(quoteLine.total, quoteLine.currency ?? currency)}`}
            </Typography>
          )}
        </Box>
        {allowPriceOverride && (
          <TextField
            label={`Price for ${row.name}`}
            size="small"
            value={
              priceEdit ??
              (selected && quoteLine !== null ? majorPriceInput(quoteLine.base) : '')
            }
            onChange={(event) => onPriceChange(event.target.value)}
            disabled={disabled || !selected || (quoteLine === null && priceEdit === undefined)}
            error={priceInvalid}
            helperText={priceInvalid ? 'Enter a valid price greater than 0.' : ' '}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">{currency}</InputAdornment>,
              },
              htmlInput: { inputMode: 'decimal', autoComplete: 'off' },
            }}
            sx={{ width: 190 }}
          />
        )}
      </Stack>
    </Box>
  );
}

/**
 * Convert the operator's major-unit entry to the contract's integer minor units.
 * This checks only whether the value can be represented on the wire; range and PIN
 * decisions remain exclusively server-side.
 */
/**
 * The money footer — the corrected tax behavior. Every figure is the server's, and a
 * deferred tax renders as a NOTE, never as `$0.00` and never as a total.
 */
function MoneyFooter({
  quoteState,
  quoteRetry,
  empty,
}: {
  quoteState: QuoteState;
  quoteRetry: () => void;
  empty: boolean;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  if (empty) {
    return (
      <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.62) }}>
        Add an item to price this sale.
      </Typography>
    );
  }
  if (quoteState.status === 'blocked') {
    return (
      <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.62) }}>
        Fix the negotiated package price to re-price this cart.
      </Typography>
    );
  }
  if (quoteState.status === 'awaiting-override-pin') {
    return (
      <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.62) }}>
        Enter the override PIN to price this transaction.
      </Typography>
    );
  }
  if (quoteState.status === 'error') {
    return (
      <Alert
        severity="error"
        role="alert"
        action={
          quoteState.retryable ? (
            <Button color="inherit" size="small" onClick={quoteRetry}>
              Retry
            </Button>
          ) : undefined
        }
      >
        {quoteState.message}
      </Alert>
    );
  }
  if (quoteState.status !== 'ready') {
    return (
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
        <CircularProgress size={18} />
        <Typography variant="body2">Pricing the cart...</Typography>
      </Stack>
    );
  }

  const { quote } = quoteState;
  const currency = quote.currency ?? FALLBACK_CURRENCY;
  return (
    <Box sx={{ border: `1px solid ${t.line}`, borderRadius: 1, bgcolor: t.surface, p: 1.5 }}>
      <Stack spacing={0.75}>
        <MoneyRow label="Subtotal" value={formatMoney(quote.subtotal, currency)} />
        {quote.tax !== null && quote.total !== null ? (
          <>
            <MoneyRow label="Tax" value={formatMoney(quote.tax, currency)} />
            <Divider />
            <MoneyRow label="Total" value={formatMoney(quote.total, currency)} strong />
          </>
        ) : (
          /* The server could not resolve the jurisdiction, so no tax and no total
             exist yet. Say that — never print the null as $0.00, and never present
             the pre-tax subtotal as the amount due. */
          <Typography sx={{ fontSize: 13, color: inkAlpha(theme, 0.62) }}>
            Tax calculated at checkout
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function MoneyRow({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  const theme = useTheme();
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
      <Typography sx={{ fontSize: 13, color: inkAlpha(theme, 0.62) }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: strong ? 600 : 400 }}>{value}</Typography>
    </Stack>
  );
}

/**
 * The purchase confirmation (DNKADM-0250) — what a settled sale leaves on screen in
 * place of the cart. The headline is the amount actually CHARGED: the receipt's
 * `grandTotal` is the TRUTH, so if it differs from the quoted total this is the figure
 * shown, and the quote's total is never presented as the charge. The receipt detail
 * beneath it is unchanged — a line with no `tier` is labelled "Name unavailable" rather
 * than treated as an error, and the order's own `status` is stated rather than implied,
 * so a settled-but-not-`COMPLETED` order still reads truthfully.
 *
 * `role="status"` stays on the container: the confirmation appears without a navigation,
 * so it must be announced.
 */
function SellConfirmation({ order }: { order: MembershipOrder }) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const currency = order.currency ?? FALLBACK_CURRENCY;
  return (
    <Stack role="status" spacing={2} sx={{ pt: 1 }}>
      <Stack spacing={0.5} sx={{ alignItems: 'center', textAlign: 'center' }}>
        <Avatar
          variant="rounded"
          sx={{ bgcolor: t.brandSoft, color: t.brand, width: 44, height: 44 }}
        >
          <Icon name="check_circle" sx={{ fontSize: 26 }} />
        </Avatar>
        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>Purchase complete</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 28, fontWeight: 600 }}>
          {formatMoney(order.grandTotal, currency)}
        </Typography>
        <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.62) }}>
          Charged to this member
        </Typography>
      </Stack>
      <Box sx={{ border: `1px solid ${t.line}`, borderRadius: 1, p: 1.5, bgcolor: t.surface }}>
        <Stack spacing={1}>
          <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
            <Typography sx={{ fontSize: 13, color: inkAlpha(theme, 0.62) }}>Order</Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 13 }}>
              {order.orderNumber ?? (order.orderId === null ? PLACEHOLDER : String(order.orderId))}
            </Typography>
          </Stack>
          <OrderStatusRow status={order.status} />
          <Divider />
          {order.lines.map((line, index) => (
            <MoneyRow
              key={`${line.type ?? 'line'}-${line.refId ?? index}`}
              label={scrubBrand(line.tier) ?? 'Name unavailable'}
              value={formatMoney(line.total, currency)}
            />
          ))}
          <Divider />
          <MoneyRow label="Subtotal" value={formatMoney(order.subtotal, currency)} />
          <MoneyRow label="Tax" value={formatMoney(order.taxTotal, currency)} />
          <MoneyRow label="Total" value={formatMoney(order.grandTotal, currency)} strong />
          {order.compValue !== null && (
            <MoneyRow label="Comp value" value={formatMoney(order.compValue, currency)} />
          )}
        </Stack>
      </Box>
    </Stack>
  );
}

// --- Catalog → rows ------------------------------------------------------------

/**
 * A server purchase cap, sanitized: `null`/absent stays uncapped, anything else is
 * floored to a non-negative integer. `maxPurchaseQuantity` is carried VERBATIM from
 * the server (DNKADM-0241 U1), so a `0`, a negative, or a fraction can reach the UI —
 * a cap below 1 bounds the stepper to `[0, 0]`, i.e. the row cannot be sold at all.
 */
function sanitizeCap(value: number | null | undefined): number | null {
  if (value == null) return null;
  if (!Number.isFinite(value)) return null;
  return Math.max(0, Math.floor(value));
}

/** Bound a quantity to `[0, cap]`; a negative is never reachable. */
function boundQuantity(next: number, cap: number | null): number {
  const floored = Math.max(0, Math.floor(Number.isFinite(next) ? next : 0));
  return cap === null ? floored : Math.min(floored, cap);
}

/**
 * The quantity a row actually carries — clamped at the READ boundary, not only when
 * the stepper writes it. Two paths reach the state without ever passing through
 * {@link setQuantity}: `seedCreditBucketId` seeds `1` before the catalog (and its cap)
 * has loaded, and a catalog Retry can return a LOWER cap than the quantity already
 * entered. Clamping here means the invariant holds however a quantity got into state,
 * so an over-cap line can never enter the cart or be quoted.
 */
function quantityFor(quantities: Record<string, number>, row: BucketRow): number {
  return boundQuantity(quantities[row.key] ?? 0, row.cap);
}

/** `soldCreditExpiryDays` as a hint; `0` is the meaningful "never expires". */
function expiryHint(days: number | null | undefined): string | null {
  if (days == null || !Number.isFinite(days)) return null;
  if (days <= 0) return 'never expires';
  return `expires ${Math.floor(days)} days after purchase`;
}

/**
 * A sellable credit-bucket row, or nothing. Hidden: an UNPRICED bucket (it would
 * `400` the whole quote) and one whose id is not a numeric `refId` (it cannot be
 * ordered). Returns an array so it composes with `flatMap`.
 */
function toBucketRow(bucket: CreditBucket): BucketRow[] {
  const price = bucket.pricePerCredit;
  if (price == null || price <= 0) return [];
  const refId = Number(bucket.id);
  if (!Number.isFinite(refId)) return [];
  return [
    {
      key: bucket.id,
      refId,
      name: scrubBrand(bucket.name) ?? PLACEHOLDER,
      pricePerCredit: price,
      cap: sanitizeCap(bucket.maxPurchaseQuantity),
      expiryHint: expiryHint(bucket.soldCreditExpiryDays),
    },
  ];
}

/**
 * A sellable package row, or nothing. Hidden: an unpriced or free package, a DRAFT
 * (published-only is purchasable), a deactivated row, and one that is not offered at
 * the selected location (`locationIds` empty ⇒ everywhere; otherwise it must contain
 * the selected id, compared as strings).
 *
 * **Sellable = a POSITIVE price.** `null` is UNPRICED (the quote would reject the whole
 * cart with a 400) and `0` is a genuinely FREE package, which is deliberately NOT sold
 * on a priced cart — free is given away with a comp order (Operator decision,
 * DNKADM-0245; mirrors the `price > 0 ? 'Sellable' : 'Comp / manual'` convention in
 * `PlansTab`). The three states are distinct at the parse boundary since DNKADM-0245;
 * this is where the product rule is applied.
 */
function toPackageRow(offering: Offering, selectedLocationId: string | null): PackageRow[] {
  if (offering.price == null || offering.price <= 0) return [];
  if (offering.status === 'DRAFT' || !offering.active) return [];
  if (
    offering.locationIds.length > 0 &&
    (selectedLocationId === null || !offering.locationIds.includes(String(selectedLocationId)))
  ) {
    return [];
  }
  const refId = Number(offering.id);
  if (!Number.isFinite(refId)) return [];
  return [
    {
      key: offering.id,
      refId,
      name: scrubBrand(offering.name) ?? PLACEHOLDER,
      price: offering.price,
      grants: offering.credits.map(
        (credit) => `${credit.amount} ${scrubBrand(credit.bucketName) ?? 'credits'}`,
      ),
    },
  ];
}

// --- Quote / order helpers ------------------------------------------------------

/**
 * The quote row for one cart row, keyed on `(type, refId)`. The type must be part of
 * the key: a bucket and an offering can share a numeric id, and a cross-type match
 * would print the wrong money against a row. A typeless server line is matched on
 * `refId` alone as a fallback.
 */
function findQuoteLine(
  quote: MembershipQuote,
  type: 'credit' | 'package',
  refId: number,
): MembershipQuoteLine | null {
  return (
    quote.lines.find((line) => line.refId === refId && line.type === type) ??
    quote.lines.find((line) => line.refId === refId && line.type === null) ??
    null
  );
}

/**
 * The per-row amount, straight from the quote — NEVER recomputed. A deferred line has
 * no total, so its pre-tax `base` is shown (the footer carries the deferral note for
 * the cart as a whole); a row the server did not price shows nothing.
 */
function rowAmountLabel(
  quote: MembershipQuote | null,
  type: 'credit' | 'package',
  refId: number,
  currency: string,
): string | null {
  if (quote === null) return null;
  const line = findQuoteLine(quote, type, refId);
  if (line === null) return null;
  return formatMoney(line.total ?? line.base, line.currency ?? currency);
}

function compPayment(
  locationId: number,
  idempotencyKey: string,
  reason: string,
): CompPaymentInput {
  const payment: CompPaymentInput = { mode: 'comp', locationId, idempotencyKey };
  const trimmed = reason.trim();
  if (trimmed.length > 0) payment.reason = trimmed;
  return payment;
}

function externalPayment(
  locationId: number,
  idempotencyKey: string,
  status: ExternalStatus,
  paymentMethodId: string,
): ExternalPaymentInput {
  const payment: ExternalPaymentInput = { mode: 'external', locationId, idempotencyKey, status };
  // The method id is required when the sale is already PAID, and meaningless otherwise.
  if (status === 'paid' && paymentMethodId.trim().length > 0) {
    payment.paymentMethodId = paymentMethodId.trim();
  }
  return payment;
}

function submitButtonLabel(
  mode: PaymentMode,
  submitting: boolean,
  quote: MembershipQuote | null,
): string {
  if (submitting) return mode === 'card' ? 'Charging...' : 'Creating...';
  if (mode === 'comp') return 'Create comp order';
  if (mode === 'external') return 'Record payment';
  // A deferred-tax quote has no total, and the pre-tax subtotal must NEVER be
  // labelled as the charge — the button then reads plainly.
  if (quote !== null && quote.total !== null) {
    return `Pay ${formatMoney(quote.total, quote.currency ?? FALLBACK_CURRENCY)}`;
  }
  return 'Pay';
}

function locationMessage(status: string): string {
  if (status === 'loading') return 'Loading locations before selling.';
  if (status === 'error') return 'Locations could not be loaded. Retry before selling.';
  return 'Select a location before selling.';
}

function terminalOrderMessage(order: MembershipOrder): string {
  const label = order.orderNumber ?? order.orderId ?? '';
  return unsettledOrderMessage(order.status, label, {
    pending: 'Order {order} is still pending. Nothing has been granted yet.',
    failed: 'Payment declined. Order {order} failed. Nothing was granted.',
  });
}

function paymentActionNote(action: MembershipOrder['paymentAction']): string {
  if (action === null) return 'Waiting for the payment to finish...';
  const redirectUrl = asActionText(action['redirectUrl'] ?? action['url']);
  if (redirectUrl !== null) {
    return 'Card authentication requires a redirect. Waiting for payment confirmation...';
  }
  return 'Card authentication is required. Waiting for payment confirmation...';
}

/**
 * Drive what a client CAN drive, in the SAME order as `BuyMembershipOrderDialog`
 * (DNKADM-0245): a top-level redirect first, then a nested `clientAction` — its own
 * redirect first, else the shared {@link getPaymentActionDriver} seam. Anything else
 * is left to the server and settled by the authoritative re-read. Nothing here claims
 * success; a driver that refuses throws {@link UnsupportedPaymentActionError}, which
 * the caller treats as "fall through to the re-read".
 */
async function drivePaymentAction(action: MembershipOrder['paymentAction']): Promise<void> {
  if (action === null) return;
  const redirectUrl = asActionText(action['redirectUrl'] ?? action['url']);
  if (redirectUrl !== null) {
    window.location.assign(redirectUrl);
    return;
  }
  const clientAction = asActionRecord(action['clientAction']);
  if (clientAction === null) return;
  const nestedRedirectUrl = asActionText(clientAction['redirectUrl'] ?? clientAction['url']);
  if (nestedRedirectUrl !== null) {
    window.location.assign(nestedRedirectUrl);
    return;
  }
  await getPaymentActionDriver()(clientAction);
}

function asActionText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text.length > 0 ? text : null;
}

/** A shallow copy of a plain-object client action, or null when it is not one. */
function asActionRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return { ...(value as Record<string, unknown>) };
}

function apiEnvelopeCode(error: ApiError): string | null {
  const code = error.details?.['code'];
  return typeof code === 'string' ? code : null;
}

/**
 * Map a failure to what the admin sees. Branches on the envelope **`code`** (and the
 * HTTP status), NEVER on message text, and surfaces the server's own `message`
 * through the shared `serverMessage` scrub seam.
 */
function toSellErrorView(
  error: unknown,
  fallbackMessage = 'The sale could not be completed.',
): SubmitError {
  if (error instanceof CardTokenizationError) {
    return {
      retryable: false,
      message: error.validation
        ? 'Finish entering the card before charging.'
        : 'The card could not be tokenized. Ask the member to check the card and try again.',
    };
  }
  const overrideView = negotiatedPriceErrorView(error);
  if (overrideView !== null) return overrideView;
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  const suffix = requestId ? ` Request ID: ${requestId}` : '';
  if (error instanceof ApiError) {
    const message = serverMessage(error);
    const envelopeCode = apiEnvelopeCode(error);
    if (
      error.status === 402 ||
      envelopeCode === 'PAYMENT_DECLINED' ||
      envelopeCode === 'CARD_DECLINED'
    ) {
      // DNKAPI-0676: a known decline reason replaces the plain decline line; OTHER/unknown keep it.
      const reasonLine = declineReasonLineFromError(error);
      return {
        retryable: false,
        message: `${reasonLine ?? message ?? 'Payment declined. Ask the member for another card.'}${suffix}`,
      };
    }
    if (envelopeCode === 'UNSUPPORTED_IN_SLICE') {
      return {
        retryable: false,
        message: `${message ?? 'That item cannot be sold in this cart. Sell a membership plan from Buy membership instead.'}${suffix}`,
      };
    }
    if (envelopeCode === 'ORDER_NOT_FOUND') {
      return {
        retryable: false,
        message: `${message ?? 'That order could not be found.'}${suffix}`,
      };
    }
    if (envelopeCode === 'INVALID_REQUEST' || error.status === 400) {
      // A rejected cart is rejected WHOLE — the rows stay on screen so the admin can
      // see and fix what the server refused; nothing is silently dropped.
      return {
        retryable: false,
        message: `${message ?? 'The cart was rejected. Check the items and try again.'}${suffix}`,
      };
    }
    if (
      (error.status === 422 || error.status === 403) &&
      envelopeCode === 'CONNECTION_NOT_AUTHORIZED'
    ) {
      const reconnect = 'Reconnect Square before accepting card payments for this location.';
      return {
        retryable: false,
        message: `${message ? `${message} ${reconnect}` : reconnect}${suffix}`,
      };
    }
    switch (error.code) {
      case 'SESSION_EXPIRED':
        return {
          retryable: false,
          message: `Your session expired. Please sign in again to continue.${suffix}`,
        };
      case 'FORBIDDEN':
      case 'NOT_FOUND':
      case 'VALIDATION':
      case 'CONFLICT':
      case 'UNKNOWN':
      case 'RATE_LIMITED':
      case 'NETWORK_ERROR':
        return {
          retryable: error.retryable === true,
          message: `${message ?? fallbackMessage}${suffix}`,
        };
    }
  }
  return { retryable: false, message: fallbackMessage };
}
